/**
 * ============================================================================
 * useAnalytics.js v3.0 - DSGVO OPT-IN
 * ============================================================================
 *
 * ✅ v3.0: DSGVO-konform - Nur tracken mit User-Einwilligung!
 * ✅ v2.4: getAccessToken() OHNE Parameter (wie in Projektdokumentation!)
 * ✅ v2.3: ESLint Fix - Hook nicht mehr in try/catch
 * ✅ v2.2: Besseres Error Handling + Debug Logging
 * ✅ v2.1: Öffentliches Page Tracking
 *
 * @version 3.0.0 - DSGVO Opt-In
 */

import { useCallback, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useAuth } from './useAuth';
import { hasAnalyticsConsent } from '../utils/cookieConsent';

// API URL mit Fallback
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.planivio.de';
const BATCH_INTERVAL = 10000;
const MAX_BATCH_SIZE = 20;

// Platform Detection (web, android, ios)
const APP_PLATFORM = Capacitor.getPlatform();

console.log('📊 useAnalytics.js v2.4 loaded');
console.log('📊 API_BASE_URL:', API_BASE_URL);

// ============================================================================
// GLOBALER EVENT QUEUE (Singleton)
// ============================================================================
let eventQueue = [];
let batchTimer = null;
let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
let globalTokenRef = null;

// Session ID für anonymes Tracking
const getSessionId = () => {
    try {
        if (typeof sessionStorage === 'undefined') return 'server';
        
        let sessionId = sessionStorage.getItem('planivio_session');
        if (!sessionId) {
            sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
            sessionStorage.setItem('planivio_session', sessionId);
        }
        return sessionId;
    } catch {
        return `fallback-${Date.now()}`;
    }
};

// UTM Parameter aus URL extrahieren
const getUTMParams = () => {
    try {
        if (typeof window === 'undefined') return {};
        
        const params = new URLSearchParams(window.location.search);
        return {
            utm_source: params.get('utm_source') || '',
            utm_medium: params.get('utm_medium') || '',
            utm_campaign: params.get('utm_campaign') || ''
        };
    } catch {
        return {};
    }
};

// Online/Offline Listener
if (typeof window !== 'undefined') {
    window.addEventListener('online', () => { isOnline = true; });
    window.addEventListener('offline', () => { isOnline = false; });
    
    window.addEventListener('beforeunload', () => {
        if (eventQueue.length > 0 && globalTokenRef) {
            try {
                navigator.sendBeacon(
                    `${API_BASE_URL}/api/analytics/track/batch`,
                    new Blob([JSON.stringify({ events: eventQueue })], { type: 'application/json' })
                );
            } catch { /* ignore */ }
        }
    });
}

// Queue an Backend senden - ✅ v3.0: Mit Consent-Prüfung!
async function flushQueue(token) {
    if (eventQueue.length === 0 || !isOnline || !token) return;

    // ✅ v3.0: Consent-Prüfung beim Flush!
    if (!hasAnalyticsConsent()) {
        console.log('🔒 Batch flush BLOCKED (no consent) - clearing queue');
        eventQueue = []; // Queue leeren, da keine Einwilligung
        return;
    }

    const eventsToSend = eventQueue.splice(0, MAX_BATCH_SIZE);

    try {
        const response = await fetch(`${API_BASE_URL}/api/analytics/track/batch`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ events: eventsToSend })
        });

        if (!response.ok) {
            // Bei Fehler zurück in Queue
            eventQueue.unshift(...eventsToSend);
            console.warn('📊 Batch flush failed:', response.status);
        } else {
            console.log(`📊 Batch flushed: ${eventsToSend.length} events`);
        }
    } catch (err) {
        // Bei Netzwerkfehler zurück in Queue
        eventQueue.unshift(...eventsToSend);
        console.warn('📊 Batch flush error:', err.message);
    }
}

// ============================================================================
// ÖFFENTLICHES PAGE TRACKING - NUR MIT CONSENT! (DSGVO v3.0)
// ============================================================================

/**
 * Trackt eine Seite anonym (für Landing Page, Features, etc.)
 * ✅ v3.0: NUR wenn Analytics-Consent vorhanden!
 */
export const trackPublicPageView = async (page) => {
    if (!page) return;

    // ✅ v3.0: Consent-Prüfung!
    if (!hasAnalyticsConsent()) {
        console.log(`🔒 trackPublicPageView BLOCKED (no consent): ${page}`);
        return;
    }

    console.log(`📊 trackPublicPageView: ${page}`);

    try {
        const response = await fetch(`${API_BASE_URL}/api/analytics/track/pageview`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                page: String(page).substring(0, 100),
                referrer: typeof document !== 'undefined' ? document.referrer : '',
                session_id: getSessionId(),
                platform: APP_PLATFORM,
                ...getUTMParams()
            })
        });

        if (response.ok) {
            console.log(`✅ PageView tracked: ${page}`);
        } else {
            console.warn(`❌ PageView failed: ${response.status}`);
        }
    } catch (err) {
        console.warn('❌ PageView error:', err.message);
    }
};

/**
 * Hook für öffentliche Seiten (Landing, Features, etc.)
 */
export function usePublicPageView(pageName) {
    const hasTrackedRef = useRef(false);
    
    useEffect(() => {
        if (!hasTrackedRef.current && pageName) {
            hasTrackedRef.current = true;
            trackPublicPageView(pageName);
        }
    }, [pageName]);
}

// ============================================================================
// MAIN HOOK: useAnalytics (für eingeloggte User)
// ============================================================================
export function useAnalytics() {
    // ✅ v2.3: Hook wird IMMER aufgerufen (nicht bedingt!)
    const { getAccessToken, isAuthenticated } = useAuth();
    const tokenRef = useRef(null);
    const isInitializedRef = useRef(false);
    
    // Token holen und cachen
    useEffect(() => {
        if (isAuthenticated && !isInitializedRef.current) {
            isInitializedRef.current = true;
            
            // ✅ v2.4: OHNE Parameter! (wie in Projektdokumentation)
            getAccessToken()
                .then(token => {
                    tokenRef.current = token;
                    globalTokenRef = token;
                    console.log('📊 Analytics token acquired');
                })
                .catch(err => {
                    console.warn('📊 Token error:', err.message);
                });
        }
    }, [isAuthenticated, getAccessToken]);
    
    // Batch Timer starten
    useEffect(() => {
        if (!batchTimer) {
            batchTimer = setInterval(() => {
                if (globalTokenRef) flushQueue(globalTokenRef);
            }, BATCH_INTERVAL);
        }
        return () => {};
    }, []);
    
    // EVENT TRACKING - ✅ v3.0: Mit Consent-Prüfung!
    const trackEvent = useCallback((eventName, metadata = {}) => {
        if (!eventName) return;

        // ✅ v3.0: Consent-Prüfung!
        if (!hasAnalyticsConsent()) {
            console.log(`🔒 trackEvent BLOCKED (no consent): ${eventName}`);
            return;
        }

        let component = 'App';
        let actualMetadata = metadata;

        // Support für trackEvent('name', 'Component') Format
        if (typeof metadata === 'string') {
            component = metadata;
            actualMetadata = arguments[2] || {};
        } else if (metadata.component) {
            component = metadata.component;
        }

        eventQueue.push({
            type: 'event',
            event_name: String(eventName).substring(0, 100),
            component: String(component).substring(0, 100),
            metadata: typeof actualMetadata === 'object' ? { ...actualMetadata, platform: APP_PLATFORM } : { platform: APP_PLATFORM },
            timestamp: new Date().toISOString()
        });

        console.log(`📊 Event: ${component}/${eventName} [${APP_PLATFORM}]`);
    }, []);
    
    // FEATURE TRACKING
    const trackFeature = useCallback((featureName, component = 'App') => {
        trackEvent('feature_used', { component, feature: featureName });
    }, [trackEvent]);
    
    // ERROR TRACKING - ✅ v3.0: Mit Consent-Prüfung!
    const trackError = useCallback((errorType, errorCode, component, action = '') => {
        // ✅ v3.0: Consent-Prüfung!
        if (!hasAnalyticsConsent()) {
            console.log(`🔒 trackError BLOCKED (no consent): ${errorType}`);
            return;
        }

        eventQueue.push({
            type: 'error',
            event_name: 'error',
            component: String(component || 'Unknown').substring(0, 100),
            metadata: {
                error_type: String(errorType || 'UnknownError').substring(0, 100),
                error_code: String(errorCode || 'N/A').substring(0, 255),
                action: String(action || '').substring(0, 100),
                platform: APP_PLATFORM
            },
            timestamp: new Date().toISOString()
        });

        console.log(`📊 Error tracked: ${errorType} in ${component}`);
    }, []);
    
    // LOGIN TRACKING - ✅ v3.0: Mit Consent-Prüfung!
    const trackLogin = useCallback(async () => {
        // ✅ v3.0: Consent-Prüfung!
        if (!hasAnalyticsConsent()) {
            console.log('🔒 trackLogin BLOCKED (no consent)');
            return;
        }

        try {
            let token = tokenRef.current;

            if (!token) {
                // ✅ v2.4: OHNE Parameter!
                token = await getAccessToken().catch(() => null);
                if (!token) {
                    console.warn('📊 No token for login tracking');
                    return;
                }
                tokenRef.current = token;
                globalTokenRef = token;
            }

            await fetch(`${API_BASE_URL}/api/analytics/track/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    platform: APP_PLATFORM,
                    session_id: getSessionId()
                })
            });
            console.log('📊 Login tracked');
        } catch (err) {
            console.warn('📊 Login tracking failed:', err.message);
        }
    }, [getAccessToken]);
    
    // SESSION END (für beforeunload)
    const trackSessionEnd = useCallback(() => {
        if (globalTokenRef && eventQueue.length > 0) {
            try {
                navigator.sendBeacon(
                    `${API_BASE_URL}/api/analytics/track/batch`,
                    new Blob([JSON.stringify({ events: eventQueue })], { type: 'application/json' })
                );
                eventQueue = [];
                console.log('📊 Session end tracked via beacon');
            } catch { /* ignore */ }
        }
    }, []);
    
    // FORCE FLUSH (manuell Queue leeren)
    const forceFlush = useCallback(async () => {
        if (globalTokenRef) {
            await flushQueue(globalTokenRef);
        }
    }, []);
    
    return {
        trackEvent,
        trackFeature,
        trackError,
        trackLogin,
        trackSessionEnd,
        forceFlush,
        trackPublicPageView,
        getQueueLength: () => eventQueue.length
    };
}

// ============================================================================
// ZUSÄTZLICHE HOOKS
// ============================================================================

/**
 * Automatisches Page View Tracking für eingeloggte User
 */
export function usePageView(componentName) {
    const { trackEvent } = useAnalytics();
    const hasTrackedRef = useRef(false);
    
    useEffect(() => {
        if (!hasTrackedRef.current && componentName) {
            hasTrackedRef.current = true;
            trackEvent('page_view', { component: componentName });
        }
    }, [componentName, trackEvent]);
}

/**
 * Error Tracking Hook
 */
export function useErrorTracking() {
    const { trackError } = useAnalytics();
    
    const logError = useCallback((error, errorInfo, component = 'Unknown') => {
        trackError(
            error?.name, 
            error?.message, 
            component, 
            errorInfo?.componentStack?.split('\n')[1]?.trim()
        );
    }, [trackError]);
    
    return { logError };
}

/**
 * Click Tracking Hook für eine Komponente
 */
export function useClickTracking(componentName) {
    const { trackEvent } = useAnalytics();
    
    const trackClick = useCallback((buttonName, metadata = {}) => {
        trackEvent('button_click', { 
            component: componentName, 
            button: buttonName, 
            ...metadata 
        });
    }, [trackEvent, componentName]);
    
    return { trackClick };
}

// ============================================================================
// STANDALONE FUNCTION (für Nutzung außerhalb von Components)
// ============================================================================

/**
 * Tracked ein Event mit Token direkt
 * Für Nutzung in Non-React Code oder wenn Hook nicht verfügbar
 */
export async function trackAnalyticsEvent(eventName, component, metadata = {}, token = null) {
    const useToken = token || globalTokenRef;
    
    if (!useToken) {
        console.warn('📊 trackAnalyticsEvent: No token available');
        // Trotzdem in Queue speichern für späteren Flush
        eventQueue.push({
            type: 'event',
            event_name: String(eventName).substring(0, 100),
            component: String(component).substring(0, 100),
            metadata: metadata || {},
            timestamp: new Date().toISOString()
        });
        return;
    }
    
    try {
        await fetch(`${API_BASE_URL}/api/analytics/track/event`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${useToken}`
            },
            body: JSON.stringify({
                event_name: eventName,
                component: component,
                metadata: { ...metadata, platform: APP_PLATFORM }
            })
        });
        console.log(`📊 Direct event tracked: ${component}/${eventName}`);
    } catch (err) {
        console.warn('📊 Direct event failed:', err.message);
        // Fallback: In Queue speichern
        eventQueue.push({
            type: 'event',
            event_name: String(eventName).substring(0, 100),
            component: String(component).substring(0, 100),
            metadata: metadata || {},
            timestamp: new Date().toISOString()
        });
    }
}

export default useAnalytics;


